import { hasGraphqlError, useMutation } from 'Data/Api.js'
import { dateISOToDateAndTime } from 'Data/format.js'
import { useClient } from 'Data/Api.js'
import { normalizePath, useSetFlowTo } from 'Simple/Flow.js'
import { notifyError, useNotifications } from 'Logic/Notifications.js'
import differenceInMinutes from 'date-fns/differenceInMinutes'
import BigNumber from 'bignumber.js'

import checkInMutation from './check-in-mutation.graphql.js'
import assignConnectionMutation from './assign-connection-mutation.graphql.js'
import query from './query.graphql.js'
import { useDataSubmit } from 'Simple/Data.js'

export let CHECKIN_ERROR_CODES = /** @type {const} */ ({
  CANNOT_RESOLVE_PATIENT_CONNECTION: 'CANNOT_RESOLVE_PATIENT_CONNECTION',
})

function useOnAssignConnectionAction(props) {
  let [, notify] = useNotifications()
  let [, executeAssignConnectionMutation] = useMutation(
    assignConnectionMutation
  )

  return async function onClick({ connection_id, patient_id }) {
    let mutationResponse = await executeAssignConnectionMutation({
      connection_id,
      patient_id,
    })
    if (mutationResponse.error) {
      return notify(
        notifyError(
          'An error has occurred assigning the connection to the patient'
        )
      )
    }
  }
}

/** @type {import('Simple/types.js').useDataOnSubmit} */
export default function useDataOnSubmit(props) {
  let client = useClient()
  let [, notify] = useNotifications()
  let setFlowTo = useSetFlowTo(props.viewPath)
  let [, executeCheckInMutation] = useMutation(checkInMutation)
  let onAssignConnectionAction = useOnAssignConnectionAction(props)

  return async function onSubmit({ value, originalValue, change, args }) {
    let confirmed = false
    let skip_connection_check = false

    if (args.type === 'assign_connection') {
      await onAssignConnectionAction({
        patient_id: value.patient_id,
        connection_id: args.connection_id,
      })

      skip_connection_check = true
    } else if (args.type === 'confirm_check_in') {
      confirmed = true
    }

    if (!confirmed && !skip_connection_check) {
      let queryResponse = await client
        .query(query, {
          patient_id: value.patient_id,
          organization_id: value.location_id,
        })
        .toPromise()

      if (queryResponse.error) {
        return notify(notifyError('An error has occurred '))
      }

      let alert_notes = queryResponse.data.notes.map(item => ({
        note: item.note,
        createdByPerson: getPersonFullName(item.author_user?.person),
        lastModified: dateISOToDateAndTime(item?.sys_created),
      }))

      let is_patient_too_late,
        has_billing_past_due = false
      if (queryResponse.data.settings[0]?.settings) {
        let settings = queryResponse.data.settings[0].settings
        let minutes_late = differenceInMinutes(
          new Date(),
          new Date(`${value.appointment_booking.start_time}Z`)
        )
        is_patient_too_late =
          settings.max_minutes_late && minutes_late > settings.max_minutes_late

        has_billing_past_due = hasBillingPastDue(
          queryResponse.data.patient_past_due,
          settings
        )
      }

      if (is_patient_too_late || has_billing_past_due || alert_notes.length) {
        change(next => {
          if (is_patient_too_late || has_billing_past_due) {
            next.alerts.criteria = {
              is_patient_too_late,
              has_billing_past_due,
            }
          }

          next.alerts.notes = alert_notes
        })
        setFlowTo(normalizePath(props.viewPath, 'Confirmation/Content'))
        return
      }
    }
    let mutationResponse = await executeCheckInMutation({
      id: value.appointment_booking._id,
      skip_connection_check: !!args.skip_connection_check,
    })
    if (
      hasGraphqlError(
        mutationResponse,
        CHECKIN_ERROR_CODES.CANNOT_RESOLVE_PATIENT_CONNECTION
      )
    ) {
      change(next => {
        next.app_id = mutationResponse.error.graphQLErrors[0].extensions.app_id
        next.app_name =
          mutationResponse.error.graphQLErrors[0].extensions.app_name
      })

      setFlowTo(normalizePath(props.viewPath, 'AssignConnection/Content'))

      return
    }

    if (mutationResponse.error) {
      return notify(
        notifyError('Cannot check in selected appointment. Please try again.')
      )
    }

    setFlowTo(normalizePath(props.viewPath, 'Confirmation/No'))
    setFlowTo(normalizePath(props.viewPath, 'AssignConnection/No'))
  }
}

function hasBillingPastDue(due_amounts, settings) {
  if (settings.due_period === 'ANY') return false

  let due_amounts_by_private_pay = getSum(
    due_amounts.filter(
      value => value.payment_account.account_type === 'PersonPaymentAccount'
    )
  )
  let due_amounts_by_insurance = getSum(
    due_amounts.filter(
      value => value.payment_account.account_type === 'InsurancePaymentAccount'
    )
  )
  let has_bills_past_due_for_private_pay =
    getDueAmountByType(due_amounts_by_private_pay, settings.due_period) >
    settings.max_due_amount
  let has_bills_past_due_for_insurance =
    getDueAmountByType(
      due_amounts_by_insurance,
      settings.due_period_insurance
    ) > settings.max_due_amount_insurance

  return has_bills_past_due_for_private_pay || has_bills_past_due_for_insurance
}

function getDueAmountByType(value, type) {
  switch (type) {
    case 'UNDER_30':
      return value.due_now
    case 'OVER_30':
      return value.due_30_days
    case 'OVER_60':
      return value.due_60_days
    case 'OVER_90':
      return value.due_90_days
    case 'OVER_120':
      return value.due_120_days
    default:
      return 0
  }
}

function getSum(amounts) {
  let sum = amounts.reduce(
    (acc, value) => ({
      due_now: acc.due_now.plus(value.due_now),
      due_30_days: acc.due_30_days.plus(value.due_30_days),
      due_60_days: acc.due_60_days.plus(value.due_60_days),
      due_90_days: acc.due_90_days.plus(value.due_90_days),
      due_120_days: acc.due_120_days.plus(value.due_120_days),
    }),
    {
      due_now: BigNumber(0),
      due_30_days: BigNumber(0),
      due_60_days: BigNumber(0),
      due_90_days: BigNumber(0),
      due_120_days: BigNumber(0),
    }
  )

  return {
    due_now: sum.due_now.decimalPlaces(2).toNumber(),
    due_30_days: sum.due_30_days.decimalPlaces(2).toNumber(),
    due_60_days: sum.due_60_days.decimalPlaces(2).toNumber(),
    due_90_days: sum.due_90_days.decimalPlaces(2).toNumber(),
    due_120_days: sum.due_120_days.decimalPlaces(2).toNumber(),
  }
}

function getPersonFullName(person) {
  return person
    ? [person.first_name, person.middle_name, person.last_name]
        .filter(Boolean)
        .join(' ')
    : ''
}
